import { Component, OnInit, ViewChild, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { Router } from '@angular/router';

import { AuthentificationService } from '../../../providers/Global/authentification.service';
import { EditService } from '../../../providers/edit/edit.service';
import { DropzoneService } from '../../../providers/Import/dropzone.service';

import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { PlaylistVoteBaseComponent } from '../playlistVoteBase.component';

interface Stations {
  id: number;
  label: string;
  type?: 'all' | 'local' | 'webradio';
  stationsLabelIds?: string[];
}

interface MonthOption {
  label: string;
  offset: number; // 0 = mois actuel, -1 = mois précédent, etc.
  year: number;
  month: number; // 1-12
}

@Component({
  selector: 'app-edit-preselect-monthly-playlist',
  templateUrl: './editPreselectMonthlyPlaylist.component.html',
  styleUrls: ['./editPreselectMonthlyPlaylist.component.scss']
})
export class EditPreselectMonthlyPlaylistComponent extends PlaylistVoteBaseComponent implements OnInit {

  // ============================================================================
  // Références de vue (implémentation des getters abstraits de la base)
  // ============================================================================
  @ViewChild('Paginator', { static: false }) private _paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) private _sort: MatSort;
  @ViewChildren('audioHLS') private _audioPlayers: QueryList<ElementRef<HTMLAudioElement>>;
  @ViewChildren('audioExtrait') private _audioExtraits: QueryList<ElementRef<HTMLAudioElement>>;

  protected get paginator() { return this._paginator; }
  protected get sort() { return this._sort; }
  protected get audioPlayers() { return this._audioPlayers; }
  protected get audioExtraits() { return this._audioExtraits; }

  // ============================================================================
  // Permission
  // ============================================================================
  protected get requiredPermission(): string { return 'edit_winmedia_preselect_monthly_playlist'; }

  // ============================================================================
  // Colonnes du tableau
  // ============================================================================
  displayedColumns: string[] = [
    'position', 'score', 'player', 'vote', 'performer_title', 'genre', 'mood3', 'voix',
    'proprietes', 'release', 'modify', 'proprietaire', 'votes_detail', 'imedia'
  ];
  displayedColumnsEditMode: string[] = [
    'dragHandle', 'positionControls', 'position', 'score', 'player', 'vote', 'performer_title', 'genre', 'mood3', 'voix',
    'proprietes', 'release', 'modify', 'proprietaire', 'votes_detail', 'imedia'
  ];

  // ============================================================================
  // Stations (spécifique à cette page)
  // ============================================================================
  stations: Stations[] = [];
  stationSelected: Stations;

  // ============================================================================
  // Sélection par mois (spécifique à cette page)
  // ============================================================================
  monthOptions: MonthOption[] = [];
  selectedMonth: MonthOption;

  constructor(
    router: Router,
    authentificationService: AuthentificationService,
    editService: EditService,
    private dropzoneService: DropzoneService,
    dialog: MatDialog,
    snackBar: MatSnackBar,
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer
  ) {
    super(router, authentificationService, editService, dialog, snackBar, iconRegistry, sanitizer);
  }

  ngOnInit(): void {
    this.initializeMonthOptions();

    Promise.all([
      this.dropzoneService.getWinmediaData(),
      this.editService.getRadioStations()
    ])
      .then(([winmediaData, radioStations]) => {
        const enrichedStations = this.mergeStationsData(winmediaData.stations, radioStations);
        this.stations = this.transformStations(enrichedStations);
        this.stationSelected = this.stations[1]; // "SUN (Locales)" par défaut
        this.setTableWinmediaPreselectMonthlyPlaylist();
      })
      .catch((err) => {
        console.error('Error loading stations:', err);
        this.stations = [];
      });
  }

  // ============================================================================
  // Implémentation des méthodes abstraites
  // ============================================================================

  /**
   * Génère l'identifiant de la playlist au format "MM_YYYY"
   * Ex : "01_2026"
   */
  getPlaylistId(): string {
    const month = this.selectedMonth.month.toString().padStart(2, '0');
    return `${month}_${this.selectedMonth.year}`;
  }

  getStationId(): number {
    return this.stationSelected?.id ?? 0;
  }

  // ============================================================================
  // Logique spécifique au mois
  // ============================================================================

  initializeMonthOptions(): void {
    const monthNames = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    const now = new Date();

    for (let i = 0; i < 6; i++) {
      const targetDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthIndex = targetDate.getMonth();
      const year = targetDate.getFullYear();
      this.monthOptions.push({
        label: `${monthNames[monthIndex]} ${year}`,
        offset: -i,
        year: year,
        month: monthIndex + 1
      });
    }

    this.selectedMonth = this.monthOptions[0];
  }

  // Auto-lock par date désactivé (voir historique git / commentaire ci-dessous pour réactiver).
  // Vérifie si la playlist doit être automatiquement verrouillée.
  // Une playlist est auto-verrouillée à partir du 15 du mois suivant.
  // isAutoLocked(): boolean {
  //   if (!this.selectedMonth) { return false; }
  //
  //   const now = new Date();
  //   const playlistMonth = this.selectedMonth.month;
  //   const playlistYear = this.selectedMonth.year;
  //
  //   let nextMonth = playlistMonth + 1;
  //   let nextYear = playlistYear;
  //   if (nextMonth > 12) {
  //     nextMonth = 1;
  //     nextYear++;
  //   }
  //
  //   const lockDate = new Date(nextYear, nextMonth - 1, 15, 0, 0, 0);
  //   return now >= lockDate;
  // }

  isCurrentMonth(): boolean {
    return this.selectedMonth && this.selectedMonth.offset === 0;
  }

  setTableWinmediaPreselectMonthlyPlaylist(): void {
    const stationsLabelIds = this.stationSelected?.stationsLabelIds || [];
    this.loadDataForSelection(
      () => this.editService.getWinmediaPreselectMonthlyPlaylist(
        stationsLabelIds,
        this.selectedMonth.year,
        this.selectedMonth.month
      )
    );
  }

  // ============================================================================
  // Chargement et transformation des stations
  // ============================================================================

  /**
   * Croise les stations WinMedia avec radio_stations pour :
   * - Filtrer uniquement les stations actives de radio_stations
   * - Utiliser le nom de radio_stations (ex: "SUN Nantes")
   * - Conserver le stationLabelId depuis WinMedia (ex: "S01")
   */
  mergeStationsData(winmediaStations: any[], radioStations: any[]): any[] {
    const winmediaMap = new Map<number, string>();
    for (const station of winmediaStations) {
      winmediaMap.set(station.id, station.label);
    }

    const enrichedStations = [];
    for (const radioStation of radioStations) {
      const winmediaLabel = winmediaMap.get(radioStation.winmedia_id);
      if (winmediaLabel) {
        const stationLabelId = winmediaLabel.slice(0, 3);
        enrichedStations.push({
          id: radioStation.winmedia_id,
          label: radioStation.name,
          stationLabelId: stationLabelId
        });
      }
    }

    return enrichedStations;
  }

  /**
   * Transforme la liste des stations :
   * - Regroupe les stations locales (S01, S02, ...) en une seule entrée "SUN (Nantes, Cholet, ...)"
   * - Conserve les webradios (W01, W02, ...) individuellement
   */
  transformStations(rawStations: any[]): Stations[] {
    const localStationsLabelIds: string[] = [];
    const localNames: string[] = [];
    const webradios: Stations[] = [];

    for (const station of rawStations) {
      const stationCode = station.stationLabelId;
      const prefix = stationCode?.slice(0, 1);

      if (prefix === 'S') {
        localStationsLabelIds.push(stationCode);
        const cityName = this.extractCityNameFromLabel(station.label);
        if (cityName) { localNames.push(cityName); }
      } else if (prefix === 'W') {
        webradios.push({
          id: station.id,
          label: station.label,
          type: 'webradio',
          stationsLabelIds: [stationCode]
        });
      }
    }

    const localLabel = localNames.length > 0
      ? `SUN (${localNames.join(', ')})`
      : 'SUN (Locales)';

    return [
      { id: 0, label: 'Toutes les stations', type: 'all', stationsLabelIds: [] },
      { id: -1, label: localLabel, type: 'local', stationsLabelIds: localStationsLabelIds },
      ...webradios
    ];
  }

  /**
   * Extrait le nom de la ville depuis le label de radio_stations
   * Ex: "SUN Nantes" -> "Nantes"
   */
  extractCityNameFromLabel(label: string): string {
    const match = label.match(/^SUN\s+(.+)$/i);
    if (match && match[1]) { return match[1]; }
    return label;
  }
}
